import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import {
  createVehicleInspection,
  getVehiclefinanceHistory,
  getVehicleFinfinanceVendors,
  getVehicleInfo,
  VehicleRequestCancellation,
} from "store/api/vehicle-finance";
import { VehicleCancellationProp, VehicleInfoProp, VehiclePaginationProp } from "styles/types/vehicle";
import { RequestVehicleInspection } from "styles/types/vehicle";

export const getUserVehicleDetail = createAsyncThunk(
  "vehicle/vehicleInfo",
  async (paymentCode: VehicleInfoProp, { rejectWithValue }) => {
    try {
      const response = await getVehicleInfo(paymentCode);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const makeVehicleInspection = createAsyncThunk(
  "vehicle/vehicleInspection",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await createVehicleInspection(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const getAllVehicleVendors = createAsyncThunk(
  "vehicle/vehicleVendors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getVehicleFinfinanceVendors();
      return response?.Data?.Items;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const getAllVehicleHistory = createAsyncThunk(
  "vehicle/vehicleHistory",
  async (payload:VehiclePaginationProp, { rejectWithValue }) => {
    try {
      const response = await getVehiclefinanceHistory(payload);
       return response?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
)

export const cancellVehicleRequest = createAsyncThunk(
  "vehicle/vehicleCancellation",
  async (payload:VehicleCancellationProp, { rejectWithValue }) => {
    try {
      const response = await VehicleRequestCancellation(payload);
       return response?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
)

export const paymentCodeSlice = createAsyncThunk(
  "vehicle/paymentCode",
  (paymentCode: any) => {
    return  paymentCode ;
  }
);

type VehicleFinanceState = {
  vehicleInfo: any;
  vehicleInspection: any;
  vehicleHistory: any;
  vehicleVendors: any;
  vehicleCancell: any;
  paymentCode:any;
  isLoading: boolean;
  error: string | null;
};

const initialState: VehicleFinanceState = {
  isLoading: false,
  error: null,
  vehicleInfo: '',
  vehicleCancell: {},
  vehicleVendors: [],
  vehicleInspection: {},
  paymentCode:'',
  vehicleHistory: []
};

const vehicleFinanceSlice = createSlice({
  name: "vehicle-finance",
  initialState: initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getUserVehicleDetail.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(getUserVehicleDetail.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.vehicleInfo = action.payload;
    });

    builder.addCase(getUserVehicleDetail.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(getAllVehicleHistory.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(getAllVehicleHistory.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.vehicleHistory= action.payload;
    });

    builder.addCase(getAllVehicleHistory.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(makeVehicleInspection.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(makeVehicleInspection.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.vehicleInfo = action.payload;
    });

    builder.addCase(makeVehicleInspection.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(getAllVehicleVendors.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(getAllVehicleVendors.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.vehicleVendors = action.payload;
    });

    builder.addCase(getAllVehicleVendors.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(cancellVehicleRequest.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(cancellVehicleRequest.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.vehicleCancell = action.payload;
    });

    builder.addCase(cancellVehicleRequest.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(paymentCodeSlice.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(paymentCodeSlice.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.paymentCode = action.payload;
    });

    builder.addCase(paymentCodeSlice.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });
  },
});

export default vehicleFinanceSlice.reducer;
